import { calculateHeaderGroupHeight, calculateAnnouncementBarHeightAbove } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Update announcement-bar height above header
    const headerGroup = document.querySelector('#header-group');
    const announcementBarHeightAbove = calculateAnnouncementBarHeightAbove(this, headerGroup);
    document.body.style.setProperty('--announcement-bar-height-above', `${announcementBarHeightAbove}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    const drawer = this.refs.headerDrawerContainer;
    const menu = this.refs.headerMenu;
    if (!(drawer instanceof HTMLElement) || !(menu instanceof HTMLElement)) return;

    // Onder 990px nooit desktop:hidden op de hamburger (header-menu is
    // mobile:hidden — beide hidden = geen menu). Desktop: hamburger blijft weg.
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    if (!desktop) {
      drawer.classList.remove('desktop:hidden', 'hidden--desktop');
      if (hideMenu) {
        this.#menuDrawerHiddenWidth = window.innerWidth;
        menu.classList.add('hidden');
      } else {
        this.#menuDrawerHiddenWidth = null;
        menu.classList.remove('hidden');
      }
      return;
    }

    drawer.classList.add('desktop:hidden', 'hidden--desktop');
    this.#menuDrawerHiddenWidth = null;
    menu.classList.remove('hidden');
  }

  /**
   * Pending requestAnimationFrame id, used to coalesce a burst of scroll
   * events into a single update per animation frame.
   * @type {number | null}
   */
  #scrollRaf = null;

  /**
   * Minimum scroll distance (px) before reacting to a direction change.
   * Prevents the sticky state from flickering on tiny/jittery reversals
   * during a swipe.
   * @constant {number}
   */
  #scrollThreshold = 8;

  // HDS: scroll handler only schedules an rAF; the actual work runs once per
  // frame in #updateStickyState. This avoids a forced synchronous reflow on
  // every scroll event (the cause of mobile scroll jitter — see SHO-7629).
  #handleWindowScroll = () => {
    if (this.#scrollRaf != null) return;
    this.#scrollRaf = requestAnimationFrame(this.#updateStickyState);
  };

  #updateStickyState = () => {
    this.#scrollRaf = null;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    // READ PHASE — gather all layout reads up front, before any writes, so we
    // never force a synchronous reflow midway through the handler.
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const delta = scrollTop - this.#lastScrollTop;

    // HDS: een scroll die de pagina zelf doet om iets op zijn plek te houden (bv. «Maak je vloer
    // compleet» als een blok erboven dichtklapt, floor-complete.js) is geen scrollrichting van de
    // klant: niet tonen of verbergen, alleen de positie bijwerken (anders flikkert de header).
    if (document.documentElement.hasAttribute('data-hds-scroll-anchor')) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    // Hysteresis: ignore sub-threshold reversals so the header doesn't flip
    // show/hide state on every frame during a swipe. #lastScrollTop is left
    // untouched so small movements accumulate until they cross the threshold.
    if (Math.abs(delta) < this.#scrollThreshold) return;

    const isScrollingUp = delta < 0;
    const headerTop = this.getBoundingClientRect().top;

    // WRITE PHASE
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (headerTop >= 0) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (headerTop >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRaf != null) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  if (!customElements.get('header-component')) customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height and announcement-bar height on resize of any child
  if (headerGroup) {
    const updateHeights = () => {
      calculateHeaderGroupHeight(header, headerGroup);
      const announcementBarHeightAbove = calculateAnnouncementBarHeightAbove(header, headerGroup);
      document.body.style.setProperty('--announcement-bar-height-above', `${announcementBarHeightAbove}px`);
    };

    // Initial update
    updateHeights();

    const resizeObserver = new ResizeObserver(updateHeights);

    // Observe all children of the header group (including the header itself)
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (!(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
          // Update heights when children change
          updateHeights();
        }
      }
    });

    document.addEventListener('announcement-bar-stack:updated', updateHeights);

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
